/**
 * DYNAMIC HEADER
 * Shows doctor identity on every page for SEO
 * Dr. Deepthi Nandan Reddy – Shoulder & Elbow Surgeon, Apollo Hospitals Financial District, Hyderabad
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, ChevronRight, ArrowRight, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { useSite } from '../contexts/SiteContext';

export const DynamicHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConditionsOpen, setIsConditionsOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const { 
    domain, 
    theme, 
    conditions,
    conditionCategories,
    doctor, 
    bodyPart,
    isMainSite
  } = useSite();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isMenuOpen) {
      setIsConditionsOpen(false);
      setExpandedCategory(null);
    }
  };

  const mainSiteUrl = 'https://drdnreddyortho.com';

  // Dynamic styles based on theme
  const brandStyle = { color: theme.primaryHex };
  const buttonStyle = { 
    backgroundColor: theme.primaryHex,
    color: '#fff'
  };

  // Use categories if available, otherwise fall back to flat list
  const hasCategories = conditionCategories && conditionCategories.length > 0;

  return (
    <header className="sticky top-0 z-50 w-full bg-white shadow-sm">
      {/* Top Bar - Doctor Identity for SEO */}
      <div className="bg-gray-900 text-white py-2 hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              {/* Doctor Name & Title */}
              <a href={mainSiteUrl} className="flex items-center gap-2 hover:text-gray-300 transition-colors">
                <img 
                  src={doctor.doctorImage} 
                  alt={doctor.name}
                  className="w-8 h-8 rounded-full object-cover border-2 border-white/20"
                />
                <div>
                  <span className="font-semibold">{doctor.name}</span>
                  <span className="text-gray-400 ml-2">– {doctor.shortTitle || 'Shoulder & Elbow Surgeon'}</span>
                </div>
              </a>
              {/* Hospital & Location */}
              <div className="flex items-center gap-1 text-gray-400">
                <MapPin className="w-3 h-3" />
                <span>{doctor.hospitalFull}, {doctor.city}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <a href={`tel:${doctor.phone}`} className="flex items-center gap-2 hover:text-white transition-colors text-gray-300">
                <Phone className="w-4 h-4" />
                <span>{doctor.phone}</span>
              </a>
              <a 
                href={doctor.bookingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1 rounded text-xs font-medium transition-colors"
                style={{ backgroundColor: theme.primaryHex }}
              >
                Book at Apollo
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              {isMainSite() ? (
                <div className="flex items-center gap-3">
                  <img 
                    src={doctor.doctorImage} 
                    alt={doctor.name}
                    className="w-10 h-10 rounded-full object-cover border-2"
                    style={{ borderColor: theme.primaryHex }}
                  />
                  <div className="text-xl font-bold">
                    <span style={brandStyle}>Dr. DN Reddy</span>
                    <span className="hidden sm:inline text-gray-500 text-sm ml-2">Orthopedic Surgeon</span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="text-xl md:text-2xl font-bold">
                    <span style={brandStyle}>{domain.split('.')[0]}</span>
                    <span className="text-gray-800">.{domain.split('.').slice(1).join('.')}</span>
                  </div>
                </div>
              )}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6">
              <Link 
                to="/" 
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                Home
              </Link>
              
              {/* Categorized Conditions Dropdown */}
              {!isMainSite() && hasCategories && (
                <div className="relative group/menu">
                  <button className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors py-2">
                    {bodyPart} Conditions
                    <ChevronDown className="w-4 h-4 group-hover/menu:rotate-180 transition-transform duration-200" />
                  </button>
                  
                  {/* Mega Menu Dropdown - with hover bridge */}
                  <div className="absolute invisible opacity-0 group-hover/menu:visible group-hover/menu:opacity-100 left-1/2 -translate-x-1/2 w-[600px] bg-white shadow-2xl border rounded-xl pt-2 transition-all duration-200 ease-out">
                    {/* Invisible bridge to prevent gap */}
                    <div className="absolute -top-2 left-0 right-0 h-4" />
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-4">
                        {conditionCategories.map((category) => (
                          <div key={category.id} className="space-y-2">
                            <div 
                              className="font-semibold text-sm px-2 py-1 rounded flex items-center gap-2"
                              style={{ color: theme.primaryHex }}
                            >
                              <span>{category.icon}</span>
                              {category.name}
                            </div>
                            <div className="space-y-0.5">
                              {category.conditions.map((condition) => (
                                <Link 
                                  key={condition.id}
                                  to={condition.slug} 
                                  className="block px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded transition-colors"
                                >
                                  {condition.title}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t text-center">
                        <Link 
                          to="/shoulder-pain" 
                          className="text-sm font-medium inline-flex items-center gap-1 hover:underline"
                          style={{ color: theme.primaryHex }}
                        >
                          Not sure? Start with Shoulder Pain Guide
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Fallback: Simple dropdown if no categories */}
              {!isMainSite() && !hasCategories && conditions?.length > 0 && (
                <div className="relative group">
                  <button className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                    {bodyPart} Conditions
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <div className="absolute hidden group-hover:block w-72 bg-white shadow-xl border rounded-lg mt-2 py-2 max-h-[70vh] overflow-y-auto">
                    {conditions.map((condition) => (
                      <Link 
                        key={condition.id}
                        to={condition.slug} 
                        className="block px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0"
                      >
                        {condition.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <Link 
                to="/faq" 
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                FAQ
              </Link>
              
              <Link 
                to="/blog" 
                className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                Health Tips
              </Link>

              {/* Link to main site */}
              {!isMainSite() && (
                <a 
                  href={mainSiteUrl}
                  className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                >
                  About Dr. Reddy
                  <ArrowRight className="w-3 h-3" />
                </a>
              )}
            </nav>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:flex items-center space-x-3">
              <a href={`tel:${doctor.phone}`} className="text-sm font-medium" style={{ color: theme.primaryHex }}>
                {doctor.phone}
              </a>
              <a href={doctor.consultationLink}>
                <Button style={buttonStyle} className="hover:opacity-90 transition-opacity">
                  Book Appointment
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <div className="flex lg:hidden items-center gap-2">
              <a href={doctor.consultationLink}>
                <Button size="sm" style={buttonStyle} className="text-xs px-3">
                  Book
                </Button>
              </a>
              <button
                onClick={toggleMenu}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <nav className="lg:hidden py-4 border-t bg-white max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="flex flex-col">
            {/* Doctor Identity - Mobile */}
            <div className="px-4 pb-4 mb-2 border-b border-gray-100">
              <a href={mainSiteUrl} className="flex items-center gap-3">
                <img 
                  src={doctor.doctorImage} 
                  alt={doctor.name}
                  className="w-12 h-12 rounded-full object-cover border-2"
                  style={{ borderColor: theme.primaryHex }}
                />
                <div>
                  <div className="font-semibold text-gray-900">{doctor.name}</div>
                  <div className="text-sm text-gray-500">{doctor.shortTitle || 'Shoulder & Elbow Surgeon'}</div>
                  <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" />
                    {doctor.hospitalFull}
                  </div>
                </div>
              </a>
            </div>

            {/* Main Links */}
            <Link 
              to="/" 
              className="py-3 px-4 text-base font-medium border-b border-gray-100" 
              onClick={toggleMenu}
            >
              Home
            </Link>
            
            {/* Categorized Conditions - Mobile */}
            {!isMainSite() && hasCategories && (
              <div className="border-b border-gray-100">
                <button 
                  onClick={() => setIsConditionsOpen(!isConditionsOpen)}
                  className="w-full py-3 px-4 text-base font-medium flex items-center justify-between"
                >
                  {bodyPart} Conditions
                  <ChevronDown className={`w-5 h-5 transition-transform ${isConditionsOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isConditionsOpen && (
                  <div className="pb-2">
                    {conditionCategories.map((category) => (
                      <div key={category.id} className="border-t border-gray-50">
                        <button
                          onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                          className="w-full py-2.5 px-4 text-sm font-medium flex items-center justify-between bg-gray-50"
                          style={{ color: theme.primaryHex }}
                        >
                          <span className="flex items-center gap-2">
                            <span>{category.icon}</span>
                            {category.name}
                          </span>
                          <ChevronRight className={`w-4 h-4 transition-transform ${expandedCategory === category.id ? 'rotate-90' : ''}`} />
                        </button>
                        
                        {expandedCategory === category.id && (
                          <div className="bg-white">
                            {category.conditions.map((condition) => (
                              <Link 
                                key={condition.id}
                                to={condition.slug} 
                                className="block py-2 px-8 text-sm text-gray-600 hover:bg-gray-50" 
                                onClick={toggleMenu}
                              >
                                {condition.title}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Link 
              to="/faq" 
              className="py-3 px-4 text-base font-medium border-b border-gray-100" 
              onClick={toggleMenu}
            >
              FAQ
            </Link>
            
            <Link 
              to="/blog" 
              className="py-3 px-4 text-base font-medium border-b border-gray-100" 
              onClick={toggleMenu}
            >
              Health Tips
            </Link>

            {/* Link to Main Site */}
            {!isMainSite() && (
              <a 
                href={mainSiteUrl}
                className="py-3 px-4 text-base font-medium border-b border-gray-100 flex items-center justify-between"
                onClick={toggleMenu}
              >
                About Dr. Reddy
                <ArrowRight className="w-4 h-4" style={{ color: theme.primaryHex }} />
              </a>
            )}

            {/* Contact Info */}
            <div className="pt-4 px-4 space-y-3">
              <a 
                href={`tel:${doctor.phone}`}
                className="flex items-center gap-3 py-3 px-4 bg-gray-50 rounded-lg"
              >
                <Phone className="w-5 h-5" style={{ color: theme.primaryHex }} />
                <div>
                  <div className="text-xs text-gray-500">Call Now</div>
                  <div className="font-semibold" style={{ color: theme.primaryHex }}>{doctor.phone}</div>
                </div>
              </a>
              
              <a href={doctor.consultationLink} onClick={toggleMenu} className="block">
                <Button style={buttonStyle} className="w-full py-6 text-base">
                  Book Appointment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              
              <a 
                href={doctor.bookingLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={toggleMenu}
                className="block"
              >
                <Button variant="outline" className="w-full py-4 text-sm" style={{ borderColor: theme.primaryHex, color: theme.primaryHex }}>
                  Book at Apollo Hospitals
                </Button>
              </a>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
};
